import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import Config from "../config";
import { useHeartbeat } from "../hooks/useHeartbeat";
import { useUser } from "./UserContext";

export const AssetContext = createContext();

const DEFECT_YEAR_FILE_MAP = Config.defectFiles;

const sortYearsDesc = (years) =>
    [...years].sort((a, b) => Number(b) - Number(a));

export const AssetProvider = ({ children }) => {
    const { user, loading: authLoading } = useUser();

    const [assets, setAssets] = useState([]);
    const [defects, setDefectsState] = useState([]);
    const [defectsByYear, setDefectsByYear] = useState({});
    const [defectsVersionByYear, setDefectsVersionByYear] = useState({});
    const [isDefectsLoading, setIsDefectsLoading] = useState(false);
    const [dataStale, setDataStale] = useState(false);

    const availableYears = sortYearsDesc(Object.keys(DEFECT_YEAR_FILE_MAP));
    const latestYear = availableYears[0] || String(new Date().getFullYear());
    const [selectedYear, setSelectedYear] = useState(latestYear);

    // Ref so the polling interval always sees the current version without
    // needing to be in its dependency array.
    const defectsVersionRef = useRef({});
    useEffect(() => {
        defectsVersionRef.current = defectsVersionByYear;
    }, [defectsVersionByYear]);

    const getDefectFilenameForYear = useCallback(
        (year) => {
            const normalizedYear = String(year || latestYear);
            return (
                DEFECT_YEAR_FILE_MAP[normalizedYear] ||
                DEFECT_YEAR_FILE_MAP[latestYear] ||
                "defect_list.xlsx"
            );
        },
        [latestYear],
    );

    const normalizeRow = (row) => {
        if (!("component" in row) && "area" in row) {
            const { area, ...rest } = row;
            return { ...rest, component: area };
        }
        return row;
    };

    // Fetches defect rows from the backend API and returns { rows, version }.
    const loadDefectDataByYear = useCallback(
        async (year) => {
            const normalizedYear = String(year || latestYear);
            const filename = getDefectFilenameForYear(normalizedYear);
            const response = await axios.get(
                `/api/defects?filename=${encodeURIComponent(filename)}`,
                { withCredentials: true },
            );
            const { data: rows, version } = response.data;
            return { rows: rows.map(normalizeRow), version };
        },
        [latestYear, getDefectFilenameForYear],
    );

    const setDefects = (updatedDefects) => {
        const normalizedYear = String(selectedYear || latestYear);
        setDefectsState(updatedDefects);
        setDefectsByYear((prev) => ({
            ...prev,
            [normalizedYear]: updatedDefects,
        }));
    };

    // Send a field-level update for a single defect row.
    // `fields` is a plain object of { fieldName: newValue } pairs.
    // Returns { success: true } or { success: false, conflict: true }.
    const patchDefectFields = useCallback(
        async (defectId, fields, year = selectedYear) => {
            const normalizedYear = String(year || latestYear);
            const filename = getDefectFilenameForYear(normalizedYear);
            const version = defectsVersionRef.current[normalizedYear];

            try {
                const response = await axios.patch(
                    `/api/defects/${encodeURIComponent(defectId)}?filename=${encodeURIComponent(filename)}`,
                    { fields, version },
                    { withCredentials: true },
                );
                setDefectsVersionByYear((prev) => ({
                    ...prev,
                    [normalizedYear]: response.data.version,
                }));
                return { success: true };
            } catch (error) {
                if (error.response?.status === 409) {
                    setDataStale(true);
                    return { success: false, conflict: true };
                }
                console.error(
                    "Error patching defect:",
                    error.response?.data || error.message,
                );
                return { success: false };
            }
        },
        [selectedYear, latestYear, getDefectFilenameForYear],
    );

    // Reload current year's data from the server and clear the stale flag.
    const refreshCurrentYear = useCallback(async () => {
        setDataStale(false);
        const normalizedYear = String(selectedYear || latestYear);
        try {
            setIsDefectsLoading(true);
            const { rows, version } = await loadDefectDataByYear(normalizedYear);
            setDefectsByYear((prev) => ({ ...prev, [normalizedYear]: rows }));
            setDefectsVersionByYear((prev) => ({
                ...prev,
                [normalizedYear]: version,
            }));
            setDefectsState(rows);
        } catch (error) {
            console.error("Error refreshing defect list:", error);
        } finally {
            setIsDefectsLoading(false);
        }
    }, [selectedYear, latestYear, loadDefectDataByYear]);

    // Clear cached defect data when the user logs out so the next login always
    // fetches fresh data instead of serving stale pre-logout state.
    useEffect(() => {
        if (!authLoading && !user) {
            setDefectsByYear({});
            setDefectsVersionByYear({});
            setDefectsState([]);
            setDataStale(false);
        }
    }, [user, authLoading]);

    // Poll the server for version changes on the active year.
    // Skips when the tab is hidden, the user has been idle for 60 s, or unauthenticated.
    const normalizedSelectedYear = String(selectedYear || latestYear);
    const pollFilename = getDefectFilenameForYear(normalizedSelectedYear);
    useHeartbeat(
        async () => {
            if (!user) return;
            try {
                const response = await axios.get(
                    `/api/defects/version?filename=${encodeURIComponent(pollFilename)}`,
                    { withCredentials: true },
                );
                const serverVersion = response.data.version;
                const localVersion = defectsVersionRef.current[normalizedSelectedYear];
                if (localVersion && serverVersion !== localVersion) {
                    setDataStale(true);
                }
            } catch {
                // Silently ignore poll failures (backend down, network hiccup, etc.)
            }
        },
        { idleAfterMs: 60_000, intervalMs: 30_000 },
    );

    // Load asset_list.xlsx (read-only, stays in frontend/public/data)
    useEffect(() => {
        const loadAssetData = async () => {
            try {
                const response = await fetch("/data/asset_list.xlsx");
                if (!response.ok) throw new Error("Error loading asset list");
                const data = await response.arrayBuffer();
                const workbook = XLSX.read(data);
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                setAssets(XLSX.utils.sheet_to_json(worksheet));
            } catch (error) {
                console.error("Error loading asset list:", error);
            }
        };
        loadAssetData();
    }, []);

    // Preload the latest year — runs on mount and again after login.
    useEffect(() => {
        if (authLoading || !user) return;
        const loadLatestDefectData = async () => {
            try {
                setIsDefectsLoading(true);
                const { rows, version } = await loadDefectDataByYear(latestYear);
                setDefectsByYear((prev) => ({ ...prev, [latestYear]: rows }));
                setDefectsVersionByYear((prev) => ({
                    ...prev,
                    [latestYear]: version,
                }));
                setDefectsState(rows);
            } catch (error) {
                console.error("Error loading latest defect list:", error);
                setDefectsState([]);
            } finally {
                setIsDefectsLoading(false);
            }
        };
        loadLatestDefectData();
    }, [latestYear, loadDefectDataByYear, user, authLoading]);

    // Load a different year when the user switches the year selector.
    useEffect(() => {
        if (authLoading || !user) return;
        const loadSelectedYearData = async () => {
            const normalizedYear = String(selectedYear || latestYear);
            const cached = defectsByYear[normalizedYear];
            if (cached) {
                setDefectsState(cached);
                return;
            }

            try {
                setIsDefectsLoading(true);
                const { rows, version } = await loadDefectDataByYear(normalizedYear);
                setDefectsByYear((prev) => ({ ...prev, [normalizedYear]: rows }));
                setDefectsVersionByYear((prev) => ({
                    ...prev,
                    [normalizedYear]: version,
                }));
                setDefectsState(rows);
            } catch (error) {
                console.error(
                    `Error loading defect list for year ${normalizedYear}:`,
                    error,
                );
                setDefectsState([]);
            } finally {
                setIsDefectsLoading(false);
            }
        };
        loadSelectedYearData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, user, authLoading]);

    const latestDefectIds = new Set(
        (defectsByYear[latestYear] || []).map((defect) =>
            String(defect.defect_id || ""),
        ),
    );

    return (
        <AssetContext.Provider
            value={{
                assets,
                defects,
                setDefects,
                patchDefectFields,
                dataStale,
                refreshCurrentYear,
                selectedYear,
                setSelectedYear,
                availableYears,
                latestYear,
                latestDefectIds,
                isDefectsLoading,
            }}
        >
            {children}
        </AssetContext.Provider>
    );
};
